import React, {ChangeEvent, FC, useCallback, useEffect, useState} from 'react';
import { useStudentService, useTranslation } from '../../services';
import {StudentFilter, StudentStatus} from '../../interfaces';
import { isEqual } from 'lodash';
import {
	Backdrop,
	Checkbox,
	CircularProgress,
	FormControlLabel, FormGroup,
	MenuItem,
	Stack,
	TablePagination,
	TextField
} from '@mui/material';
import { ManageStudentsTable } from '../../components/ManageStudentsTable';
import {Search} from "../../components/Search";

interface AdminStudentsInterface {}

const AdminManageStudents: FC<AdminStudentsInterface> = () => {
	const pageDefault = 0;
	const rowsPerPageDefault = 25;
	const orderDefault = 'desc';
	const orderByDefault = 'submitDate';

	const { t } = useTranslation();
	const { getStudents, students, meta } = useStudentService();
	const [loading, setLoading] = useState(true);
	const [filters, setFilters] = useState<any>({
		basicFlowOnly: false,
		page: pageDefault + 1,
		limit: rowsPerPageDefault,
		order: orderDefault,
		orderBy: orderByDefault,
	});
	const [page, setPage] = useState(pageDefault);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageDefault);
	const [status, setStatus] = useState<StudentStatus | ''>('');
	const [basicFlowOnly, setBasicFlowOnly] = useState(false);

	const statusOptions: Array<{ value: StudentStatus | ''; label: string }> = [
		{ value: '', label: '--//--' },
		...Object.values(StudentStatus).map((val) => ({ value: val, label: val })),
	];

	const handlerFilters = useCallback(
		(val: StudentFilter) => {
			let newFilters = { ...filters, ...val };
			Object.keys(newFilters).forEach(
				(key) => newFilters[key] === undefined && delete newFilters[key]
			);

			if (!isEqual(filters, newFilters)) {
				if (!val.page)  {
					newFilters = {
						...newFilters,
						page: 1
					}
				}
				setFilters({ ...newFilters });

			}
		},
		[filters, setFilters]
	);

	const handleChangePage = useCallback(
		(event: unknown, newPage: number) => {
			newPage !== page && setPage(newPage);
		},
		[setPage, page]
	);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newRowPerPage = parseInt(event.target.value, 10);
			if (rowsPerPage !== newRowPerPage) {
				setRowsPerPage(newRowPerPage);
				setPage(0);
			}
		},
		[rowsPerPage, setPage, setRowsPerPage]
	);

	const handleChangeStatus = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value as StudentStatus | '';
			setStatus(value);
			if (value !== '') {
				handlerFilters({ status: value });
			} else {
				handlerFilters({ status: undefined });
			}
		},
		[handlerFilters, setStatus]
	);

	const handleChangeBasicFlowOnly = useCallback(
		() => {
			setBasicFlowOnly(!basicFlowOnly)
		},
		[basicFlowOnly]
	);

	const handleSearch = useCallback(
		({ searchBy, value }: { searchBy: string; value: string }) => {
			value = value.trim();
			if (value === '') {
				handlerFilters({
					unit: undefined,
					id: undefined,
					name: undefined,
					email: undefined,
					request: undefined,
					requestEmail: undefined,
					basicFlowEmail: undefined,
					search: undefined,
				});
			} else {
				switch (searchBy) {
					case 'student ID':
						searchBy = 'id';
						break;
					case 'Student Email':
						searchBy = 'email';
						break;
					case 'responsible person email':
						searchBy = 'requestEmail';
						break;
					case 'basic flow email':
						searchBy = 'basicFlowEmail';
						break;
				}
				handlerFilters({
					unit: undefined,
					id: undefined,
					name: undefined,
					email: undefined,
					request: undefined,
					requestEmail: undefined,
					search: undefined,
					basicFlowEmail: undefined,
					[searchBy]: value,
				});
			}
		},
		[handlerFilters]
	);

	useEffect(()=>{
		handlerFilters({ basicFlowOnly });
	}, [basicFlowOnly]);

	useEffect(() => {
		(async () => {
			!loading && setLoading(true);
			await getStudents(filters);
			setLoading(false);
		})();
	}, [filters]);

	useEffect(() => {
		handlerFilters({ page: page + 1, limit: rowsPerPage });
	}, [page, rowsPerPage]);

	return (
		<>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" sx={{ m: 1 }} />
				<Stack direction="row" justifyContent="center">
					{t('Please wait...')}
				</Stack>
			</Backdrop>

			<Stack spacing={2} margin={2}>
				<Search
					onSearch={handleSearch}
					fields={['search', 'unit', 'student ID', 'request', 'name', 'Student Email', 'responsible person email', 'basic flow email']}
				/>
				<Stack direction="row" spacing={2}>
					<TextField
						select
						label={t('Status')}
						value={status}
						onChange={handleChangeStatus}
						helperText={t('Please select status')}
					>
						{statusOptions.map((option, i) => (
							<MenuItem key={`status_${i}`} value={option.value}>
								{t(option.label)}
							</MenuItem>
						))}
					</TextField>

					<FormGroup>
						<FormControlLabel control={<Checkbox onChange={handleChangeBasicFlowOnly}/>} label={t('Basic flow only')} />
					</FormGroup>

				</Stack>
			</Stack>

			<ManageStudentsTable
				data={students}
				onFilterChange={handlerFilters}
				filter={filters}
				hidden={loading}
			/>

			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				labelRowsPerPage={t('Rows per page')}
				count={meta.totalItems || students.length}
				rowsPerPage={rowsPerPage}
				showFirstButton={true}
				showLastButton={true}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	);
};

export default AdminManageStudents;
